import z from 'zod';

/**
 * A list the backend builds from a filtered Laravel collection. Filtering keeps the original keys, so
 * once an entry is dropped the collection serializes as an object keyed by index (`{"1": …}`) instead
 * of an array. Integer keys enumerate in ascending order, which preserves the server's ordering.
 */
const laravelListSchema = <T extends z.ZodType>(item: T) =>
    z.union([z.array(item), z.record(z.string(), item).transform(record => Object.values(record))]);

export const ObjectiveStates = {
    Incomplete: 'incomplete',
    Claimable: 'claimable',
    Claimed: 'claimed',
} as const;

export const objectiveStateSchema = z.enum([
    ObjectiveStates.Incomplete,
    ObjectiveStates.Claimable,
    ObjectiveStates.Claimed,
]);

export type ObjectiveState = z.infer<typeof objectiveStateSchema>;

export const objectiveSchema = z
    .object({
        id: z.number().int(),
        name: z.string(),
        action: z.string(),
        state: objectiveStateSchema,
        url: z.string().nullable(),
    })
    .describe('Objective');

export type Objective = z.infer<typeof objectiveSchema>;

export const objectiveSetSchema = z
    .object({
        id: z.number().int(),
        name: z.string(),
        state: objectiveStateSchema,
        objectives: laravelListSchema(objectiveSchema),
        resetsIn: z.string(),
    })
    .describe('ObjectiveSet');

export type ObjectiveSet = z.infer<typeof objectiveSetSchema>;

export const objectiveSetsSchema = laravelListSchema(objectiveSetSchema).describe('ObjectiveSets');

export type ObjectiveSets = z.infer<typeof objectiveSetsSchema>;

export const objectiveClaimRequestSchema = z
    .object({
        id: z.number().int(),
    })
    .describe('ObjectiveClaimRequest');

export type ObjectiveClaimRequest = z.infer<typeof objectiveClaimRequestSchema>;

export const objectiveClaimResponseSchema = z
    .object({
        boosts: z.number().int().nonnegative(),
    })
    .describe('ObjectiveClaimResponse');

export type ObjectiveClaimResponse = z.infer<typeof objectiveClaimResponseSchema>;

export const loginStreakRewardProgressSchema = z
    .object({
        boostReward: z.number().int().nonnegative(),
        claimableAt: z.number().int().nonnegative(),
        streak: z.number().int().nonnegative(),
        countedToday: z.boolean().optional(),
    })
    .describe('LoginStreakRewardProgress');

export type LoginStreakRewardProgress = z.infer<typeof loginStreakRewardProgressSchema>;

export const loginStreakAwardSchema = loginStreakRewardProgressSchema
    .extend({
        boosts: z.number().int().nonnegative(),
    })
    .describe('LoginStreakAward');

export type LoginStreakAward = z.infer<typeof loginStreakAwardSchema>;

/**
 * A Pulse reward that a trigger earns and the user claims. Text comes from the server, and `reward` is
 * an open string, so the backend can add one without a client release.
 */
export const recurringRewardSchema = z
    .object({
        reward: z.string(),
        title: z.string(),
        description: z.string(),
        action: z.string(),
        state: objectiveStateSchema,
        /** Where the row leads while the reward is unearned. */
        url: z.string().nullable(),
        boosts: z.number().int().nonnegative(),
        resetsAt: z.string().nullable(),
        secondsUntilReset: z.number().int().nonnegative().nullable(),
    })
    .describe('RecurringReward');

export type RecurringReward = z.infer<typeof recurringRewardSchema>;

export const recurringRewardsSchema = z
    .object({
        rewards: z.array(recurringRewardSchema),
    })
    .describe('RecurringRewards');

export type RecurringRewards = z.infer<typeof recurringRewardsSchema>;

export const recurringRewardClaimResponseSchema = z
    .object({
        /** Null when the claimed reward is now hidden or turned off. */
        reward: recurringRewardSchema.nullable(),
        boosts: z.number().int().nonnegative(),
        weeklyEarnedBoosts: z.number().int().nonnegative(),
    })
    .describe('RecurringRewardClaimResponse');

export type RecurringRewardClaimResponse = z.infer<typeof recurringRewardClaimResponseSchema>;
